// Bon de commande client imprimable (A4) : numéro DOCC, référence du client, quantités et prix, état des livraisons.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { marcheAPI, settingsAPI } from '../../services/api';
import { apiError, dateFr, fcfa } from '../../utils/format';
import { typeLabel } from './common';

const css = `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #e9ecef; font-family: Arial, Helvetica, sans-serif; color: #111; }
  .toolbar { position: sticky; top: 0; z-index: 2; background: #12284C; color: #fff; padding: 10px 16px; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
  .toolbar button { background: #fff; color: #12284C; border: 0; padding: 8px 14px; border-radius: 4px; font-weight: bold; cursor: pointer; }
  .toolbar label { font-size: 13px; display: flex; gap: 6px; align-items: center; cursor: pointer; }
  .page { width: 190mm; min-height: 277mm; margin: 12px auto; background: #fff; padding: 6mm; position: relative; display: flex; flex-direction: column; }
  .top { display: flex; justify-content: space-between; gap: 16px; }
  .em { font-size: 12px; width: 50%; }
  .em img { height: 60px; }
  .em .rs { font-weight: bold; font-size: 13px; margin: 6px 0; }
  .box { border: 1px solid #333; width: 48%; }
  .box .title { text-align: center; font-size: 20px; font-weight: bold; padding: 6px 0; border-bottom: 1px solid #333; }
  .box table { width: 100%; border-collapse: collapse; font-size: 12px; text-align: center; }
  .box th { background: #e6e6e6; padding: 4px; border-bottom: 1px solid #333; }
  .box td { padding: 6px; }
  .box .num-docc { font-family: 'Courier New', monospace; color: #B4232A; font-weight: bold; font-size: 14px; }
  .cli { border: 1px solid #333; border-radius: 8px; margin: 8px; padding: 8px 12px; font-size: 12.5px; line-height: 1.6; }
  .info { margin-top: 14px; font-size: 12px; display: flex; gap: 24px; flex-wrap: wrap; }
  table.lines { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12.5px; }
  table.lines th { background: #f0f0f0; border: 1px solid #333; padding: 5px; font-size: 12px; }
  table.lines td { border: 1px solid #333; padding: 5px; }
  .num { text-align: right; white-space: nowrap; }
  .totals { margin-left: auto; margin-top: 10px; border-collapse: collapse; font-size: 12.5px; }
  .totals td { border: 1px solid #333; padding: 5px 8px; }
  .totals tr:last-child td { font-weight: bold; }
  .notes { margin-top: 12px; font-size: 11.5px; white-space: pre-line; border-left: 3px solid #12284C; padding-left: 8px; }
  .sign { display: flex; justify-content: space-between; margin-top: 36px; font-size: 12px; }
  .sign div { width: 45%; border-top: 1px solid #999; padding-top: 6px; height: 80px; }
  .foot { margin-top: auto; padding-top: 14px; font-size: 9.5px; color: #555; border-top: 1px solid #ccc; text-align: center; }
  .watermark { position: absolute; top: 40%; left: 50%; transform: translate(-50%, -50%) rotate(-28deg); font-size: 90px; font-weight: bold; color: rgba(200, 0, 0, .12); white-space: nowrap; pointer-events: none; }
  @media print { body { background: #fff; } .toolbar { display: none; } .page { margin: 0; width: auto; padding: 0; } }
`;

const BCClientPrint = () => {
  const { id } = useParams();
  const [bc, setBc] = useState(null);
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [livraisons, setLivraisons] = useState(false);
  const [notes, setNotes] = useState(false);
  useEffect(() => {
    Promise.all([marcheAPI.impression(id), settingsAPI.get()])
      .then(([b, st]) => { setBc(b.data); setS(st.data); document.title = b.data.numero_docc; })
      .catch((e) => setError(apiError(e)));
  }, [id]);
  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!bc || !s) return <div style={{ padding: 24 }}>Chargement…</div>;
  const c = bc.client || {};
  const wm = bc.statut === 'ANNULE' ? 'ANNULÉ' : null;
  const legal = [s.rccm && `RC : ${s.rccm}`, s.ninea && `NINEA : ${s.ninea}`, s.capital_social && `Capital : ${s.capital_social}`].filter(Boolean).join(' — ');
  return (
    <>
      <style>{css}</style>
      <div className="toolbar">
        <strong>{bc.numero_docc}</strong><span style={{ marginRight: 'auto' }}>{c.nom}</span>
        <label><input type="checkbox" checked={livraisons} onChange={(e) => setLivraisons(e.target.checked)} /> Afficher l'état des livraisons</label>
        {bc.notes && <label><input type="checkbox" checked={notes} onChange={(e) => setNotes(e.target.checked)} /> Afficher les notes (internes)</label>}
        <button onClick={() => window.history.back()}>Retour</button>
        <button onClick={() => window.print()}>Imprimer / PDF</button>
      </div>
      <div className="page">
        {wm && <div className="watermark">{wm}</div>}
        <div className="top">
          <div className="em">
            <img src="/logo-docc.png" alt="DOCC" />
            <div className="rs">{s.raison_sociale}</div>
            {s.activite && <div>{s.activite}</div>}
            <div>{s.adresse}</div>
            {s.telephone && <div>Tél : {s.telephone}</div>}
            {s.email && <div>Email : {s.email}</div>}
          </div>
          <div className="box">
            <div className="title">Bon de commande client</div>
            <table>
              <thead><tr><th>N° DOCC</th><th>Date</th>{bc.numero_client && <th>Réf. client</th>}</tr></thead>
              <tbody><tr><td className="num-docc">{bc.numero_docc}</td><td>{dateFr(bc.date_bc)}</td>{bc.numero_client && <td>{bc.numero_client}{bc.rang ? ` (BC n°${bc.rang})` : ''}</td>}</tr></tbody>
            </table>
            <div className="cli">
              <div><b>{c.nom}</b></div>
              {c.adresse && <div>{c.adresse}{c.ville ? `, ${c.ville}` : ''}</div>}
              {!c.adresse && c.ville && <div>{c.ville}</div>}
              {c.telephone && <div>Tél : {c.telephone}</div>}
              {c.ninea && <div>NINEA : {c.ninea}</div>}
            </div>
          </div>
        </div>
        <div className="info">
          {bc.objet && <span>Objet : <b>{bc.objet}</b></span>}
          <span>Paiement : <b>{bc.delai_paiement_jours} jours</b></span>
        </div>
        <table className="lines">
          <thead>
            <tr>
              <th style={{ width: '6%' }}>N°</th><th>Désignation</th><th style={{ width: '11%' }}>Quantité</th>
              {livraisons && <><th style={{ width: '9%' }}>Livré</th><th style={{ width: '9%' }}>Reste</th></>}
              <th style={{ width: '15%' }}>Prix unitaire HT</th><th style={{ width: '16%' }}>Montant HT</th>
            </tr>
          </thead>
          <tbody>
            {bc.lignes.map((l, i) => (
              <tr key={l.pba_type}>
                <td className="num">{i + 1}</td><td>Poteau béton armé {typeLabel(l.pba_type)} <span style={{ color: '#666', fontSize: 11 }}>({l.pba_type})</span></td>
                <td className="num">{l.quantite}</td>
                {livraisons && <><td className="num">{l.livre}</td><td className="num">{Math.max(0, l.reste)}</td></>}
                <td className="num">{fcfa(l.prix_unitaire_ht)}</td><td className="num">{fcfa(l.montant_ht)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="totals"><tbody>
          <tr><td>Total HT</td><td className="num">{fcfa(bc.total_ht)} F</td></tr>
          <tr><td>TVA {bc.taux_tva} %</td><td className="num">{fcfa(bc.total_tva)} F</td></tr>
          <tr><td>Total TTC</td><td className="num">{fcfa(bc.total_ttc)} F</td></tr>
        </tbody></table>
        {livraisons && <div style={{ fontSize: 11.5, marginTop: 8 }}>État au {dateFr(new Date().toISOString().slice(0, 10))} : {bc.livre} / {bc.quantite} poteaux livrés.</div>}
        {notes && bc.notes && <div className="notes">{bc.notes}</div>}
        <div style={{ fontSize: 11.5, marginTop: 14 }}>Merci de rappeler le numéro {bc.numero_docc} sur toute correspondance. Chaque livraison fait l'objet d'un bordereau de livraison sécurisé DOCC.</div>
        <div className="sign"><div>Le client — bon pour accord (cachet et signature)</div><div>{s.raison_sociale} — la direction</div></div>
        <div className="foot">{[s.raison_sociale, s.adresse, legal].filter(Boolean).join(' · ')}</div>
      </div>
    </>
  );
};

export default BCClientPrint;
